import {FieldPolicy} from '@apollo/client';

import {assert} from 'Asserts';
import {LimitOffsetInput} from 'graphql-client/__generated/graphqlGlobalTypes';

function isLimitOffsetInput(item: unknown): item is LimitOffsetInput {
  if (item != null) {
    const limitOffsetInput = item as LimitOffsetInput;
    return limitOffsetInput.limit !== undefined && limitOffsetInput.offset !== undefined;
  }
  return false;
}

/**
 * Returns an Apollo FieldPolicy that will work with our LimitOffsetInput pagination strategy.
 *
 * This function takes in an `aggregateKey` which is used to determine what field in the query
 * is the paginated contents.  For example, if we had a GraphQL query which looked like this:
 *
 * ```
 *   query MyFolderContents($id: StrictString!, $offset: StrictInt!) {
 *     folder(id: $id, $offset) {
 *       items(limitOffsetInput: {limit: 10, offset: $offset}) {
 *         total
 *         folderContents {
 *           ...FolderContentsGraphQL
 *         }
 *       }
 *     }
 *   }
 * ```
 *
 * We could then setup a FieldPolicy for our paginated query by using an `aggregateKey` of
 * `folderContents`.
 *
 * @param aggregateKey - the field in our query which contains the paginated items
 */
export default function concatenatePaginatedQuery<
  TResult,
  T extends Record<K, Array<TResult>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  K extends keyof any,
>(aggregateKey: K): FieldPolicy<T> {
  return {
    keyArgs: false,
    merge: (existing, incoming, {args}) => {
      const merged = existing && existing[aggregateKey] ? [...existing[aggregateKey]] : [];
      assert(
        args != null && isLimitOffsetInput((args as {limitOffsetInput: LimitOffsetInput}).limitOffsetInput),
        'A paginated query should always receive an offset or limit'
      );
      const {offset = 0} = args.limitOffsetInput;
      for (let i = 0; i < incoming[aggregateKey].length; ++i) {
        merged[offset + i] = incoming[aggregateKey][i];
      }
      return {...existing, ...incoming, [aggregateKey]: merged};
    },
  };
}
